import type { PipingSystem } from '../../types/pcfExport.types';

export interface GrpcPipingSystem {
  readonly Name: string;
  readonly RevitId: number;
  readonly Props: Record<string, string>;
}

export interface NormalizedPipingSystems {
  readonly systems: readonly PipingSystem[];
  readonly skippedEmptySystemNames: readonly string[];
}

const ComponentPropsKey = '__componentProps';

function getPropValue(props: Record<string, string>, ...candidateKeys: readonly string[]): string {
  for (const key of candidateKeys) {
    if (Object.prototype.hasOwnProperty.call(props, key)) {
      return props[key] ?? '';
    }

    const matched = Object.keys(props).find((propKey) => propKey.toLowerCase() === key.toLowerCase());
    if (matched) {
      return props[matched] ?? '';
    }
  }

  return '';
}

function parseElementIdsValue(raw: string): readonly string[] {
  const trimmed = raw.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.map((id) => String(id ?? '').trim()).filter((id) => id.length > 0);
    }
  } catch {}

  return trimmed
    .split(/[,\n;|]+/)
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}

function parseElementIds(props: Record<string, string>): readonly string[] {
  const knownBuckets = [
    'ElementIds',
    'elementIds',
    'element_ids',
    'SelectedElementIds',
    'selectedElementIds',
    'Ids',
    'ids',
  ];
  const collected = new Set<string>();

  knownBuckets.forEach((key) => {
    parseElementIdsValue(getPropValue(props, key)).forEach((id) => collected.add(id));
  });
  Object.entries(props).forEach(([key, value]) => {
    if (/element[_-]?id/i.test(key)) {
      parseElementIdsValue(value).forEach((id) => collected.add(id));
    }
  });

  return [...collected];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseComponentProps(props: Record<string, string>): Record<string, string[]> {
  const raw = getPropValue(props, ComponentPropsKey);
  if (!raw.trim()) {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return {};
    }

    const componentProps: Record<string, string[]> = {};
    Object.entries(parsed).forEach(([key, value]) => {
      const nestedValues = isRecord(value) && Array.isArray(value.values) ? value.values : [];
      const values = Array.isArray(value) ? value : nestedValues;
      const normalizedValues = values
        .map((entry) => String(entry ?? '').trim())
        .filter((entry) => entry.length > 0);
      if (key.trim() && normalizedValues.length > 0) {
        componentProps[key.trim()] = normalizedValues;
      }
    });
    return componentProps;
  } catch {
    return {};
  }
}

export function normalizeCollectedPipingSystems(
  rawSystems: readonly GrpcPipingSystem[],
): NormalizedPipingSystems {
  const systems: PipingSystem[] = [];
  const skippedEmptySystemNames: string[] = [];

  rawSystems.forEach((system) => {
    const countFromProps = getPropValue(
      system.Props,
      'ElementCount',
      'elementCount',
      'element_count',
      'Count',
      'count',
    );
    const parsedCount = Number.parseInt(countFromProps, 10);
    const elementIds = parseElementIds(system.Props);
    const elementCount = Number.isFinite(parsedCount) ? parsedCount : elementIds.length;
    if (elementCount <= 0) {
      skippedEmptySystemNames.push(system.Name);
      return;
    }

    const componentProps = parseComponentProps(system.Props);
    systems.push({
      ...system.Props,
      systemName: system.Name,
      elementCount,
      status: 'Ready',
      warningCount: 0,
      errorCount: 0,
      elementIds: [...elementIds],
      componentProps,
      [ComponentPropsKey]: componentProps,
      __revitId: system.RevitId,
      __props: system.Props,
    });
  });

  return { systems, skippedEmptySystemNames };
}
